import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { blank, Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { map, Observable } from 'rxjs';
import { PublicCheckoutOrderDetailsResponse } from '../response/public-checkout-order-details.response';
import { PublicOrderProcessRequest } from '../requests/public-order-process.request';
import { PublicOrderProcessResponse } from '../response/public-order-process.response';
import { PublicOrderRedirectResponse } from '../response/public-order-redirect.response';
import { PublicOrderCollectionItemResponse } from '../response/public-order-collection-item.response';
import { OrderNumber } from '../../../shared/types/order-number';
import { PublicOrderDetailsResponse } from '../response/public-order-details.response';
import { PublicOrderShippingAddressUpdateRequest } from '../requests/public-order-shipping-address-update.request';
import {
  PublicOrderShipmentResponse,
  PublicOrderShippingMethodCollectionItemResponse,
} from '../response';

@Injectable({
  providedIn: 'root',
})
export class PublicOrderApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  /* prettier-ignore */
  public process(options: {
    orderId: Guid;
    accessToken: string;
    request?: Nillable<PublicOrderProcessRequest>;
  }): Observable<PublicOrderProcessResponse> {
    const { orderId, accessToken, request } = options;
    const path = `order/${HttpParams.OrderId}/public/${HttpParams.AccessToken}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.OrderId]: orderId,
          [HttpParams.AccessToken]: accessToken,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = isNil(request)
      ? this.provider.httpHelper.emptyBody()
      : this.provider.httpHelper.requestToBody(request, PublicOrderProcessRequest);

    return this.provider.http
      .put(url, body, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(PublicOrderProcessResponse)));
  }

  public fetchCheckoutOrderDetailsById(options: {
    orderId: Guid;
    accessToken: string;
  }): Observable<PublicCheckoutOrderDetailsResponse> {
    const { orderId, accessToken } = options;
    const path = `order/${HttpParams.OrderId}/public/${HttpParams.AccessToken}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.OrderId]: orderId,
          [HttpParams.AccessToken]: accessToken,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            PublicCheckoutOrderDetailsResponse,
          ),
        ),
      );
  }

  public fetchDetailsByNumber(options: {
    number: OrderNumber;
  }): Observable<PublicOrderDetailsResponse> {
    const { number } = options;
    const path = `order/${HttpParams.Number}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Number]: number,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(PublicOrderDetailsResponse),
        ),
      );
  }

  public redirect(param: {
    orderId: string;
    accessToken: string;
  }): Observable<PublicOrderRedirectResponse> {
    const { orderId, accessToken } = param;
    const path = `order/${HttpParams.OrderId}/public/${HttpParams.AccessToken}/redirect`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.OrderId]: orderId,
          [HttpParams.AccessToken]: accessToken,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http
      .put(url, body, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(PublicOrderRedirectResponse),
        ),
      );
  }

  public fetchCollection(options: {
    request: CollectionRequest;
    cacheable?: boolean;
  }): Observable<CollectionResponse<PublicOrderCollectionItemResponse>> {
    const path = 'order';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicOrderCollectionItemResponse,
          ),
        ),
      );
  }

  public downloadInvoice(number: OrderNumber): Observable<Blob> {
    const path = `order/${HttpParams.Number}/invoice/download`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: number,
    });

    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http.get(url, {
      context,
      responseType: 'blob',
    });
  }

  public updateShippingAddress(params: {
    orderNumber: OrderNumber;
    shipmentId: Guid;
    request: PublicOrderShippingAddressUpdateRequest;
  }): Observable<void> {
    const { orderNumber, request, shipmentId } = params;
    const path = `order/${HttpParams.Number}/shipment/${HttpParams.ShipmentId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: orderNumber,
      [HttpParams.ShipmentId]: shipmentId,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      PublicOrderShippingAddressUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public fetchShippingAddressDetails(params: {
    orderNumber: OrderNumber;
    shipmentId: Guid;
  }): Observable<PublicOrderShipmentResponse> {
    const { orderNumber, shipmentId } = params;
    const path = `order/${HttpParams.Number}/shipment/${HttpParams.ShipmentId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Number]: orderNumber,
      [HttpParams.ShipmentId]: shipmentId,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(PublicOrderShipmentResponse),
        ),
      );
  }

  public fetchAvailableShippingMethodCollection(params: {
    cacheable?: boolean;
    countryId?: Nillable<Guid>;
    orderNumber: OrderNumber;
    shipmentId: Guid;
    skipByLoading: boolean;
    request: CollectionRequest;
  }): Observable<
    CollectionResponse<PublicOrderShippingMethodCollectionItemResponse>
  > {
    const path = `order/${HttpParams.Number}/shipment/${HttpParams.ShipmentId}/shipping/method`;
    let queryParams = this.provider.httpHelper.collectionRequestToQueryParams(
      params.request,
    );

    if (!isNil(params.countryId)) {
      queryParams = { ...queryParams, countryId: params.countryId };
    }

    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .queryParams(queryParams)
        .params({
          [HttpParams.Number]: params.orderNumber,
          [HttpParams.ShipmentId]: params.shipmentId,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(params?.cacheable, false),
      )
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(params?.skipByLoading, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicOrderShippingMethodCollectionItemResponse,
          ),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
